'use client';

import type { GestureResponderEvent } from 'react-native';

import { checkIsNative } from '@scatterlab/zeta-core';
import { MouseEvent, TouchEvent, useCallback, useEffect, useRef } from 'react';

type UseLongPressProps = {
  onLongPressIn: () => void;
  onLongPressOut: () => void;
  moveThreshold?: number;
  enabled?: boolean;
};

const useLongPress = ({
  onLongPressIn,
  onLongPressOut,
  moveThreshold = 10,
  enabled = true,
}: UseLongPressProps) => {
  const touchTimeoutRef = useRef<number | null>(null);
  const touchPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleTouchStart = useCallback(
    (event: TouchEvent | GestureResponderEvent | MouseEvent) => {
      if (!enabled) return;
      if ('touches' in event) {
        touchPositionRef.current = {
          x: event.touches[0]!.clientX,
          y: event.touches[0]!.clientY,
        };
      } else if ('clientX' in event) {
        touchPositionRef.current = { x: event.clientX, y: event.clientY };
      } else {
        touchPositionRef.current = {
          x: event.nativeEvent.locationX,
          y: event.nativeEvent.locationY,
        };
      }

      touchTimeoutRef.current = +setTimeout(() => {
        onLongPressIn();
      }, 300);
    },
    [enabled, onLongPressIn],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent | GestureResponderEvent | MouseEvent) => {
      if (!touchTimeoutRef.current) return;
      const { x: startX, y: startY } = touchPositionRef.current;
      if ('touches' in event) {
        const { clientX, clientY } = event.touches[0]!;
        if (
          Math.abs(clientX - startX) < moveThreshold &&
          Math.abs(clientY - startY) < moveThreshold
        ) {
          return;
        }
      } else if ('clientX' in event) {
        const { clientX, clientY } = event;
        if (
          Math.abs(clientX - startX) < moveThreshold &&
          Math.abs(clientY - startY) < moveThreshold
        ) {
          return;
        }
      } else if ('nativeEvent' in event) {
        const { locationX, locationY } = event.nativeEvent;
        if (
          Math.abs(locationX - startX) < moveThreshold &&
          Math.abs(locationY - startY) < moveThreshold
        ) {
          return;
        }
      }
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
      onLongPressOut();
    },
    [moveThreshold, onLongPressOut],
  );

  const handleTouchEnd = useCallback(() => {
    if (!touchTimeoutRef.current) return;
    clearTimeout(touchTimeoutRef.current);
    touchTimeoutRef.current = null;
    onLongPressOut();
  }, [onLongPressOut]);

  useEffect(() => {
    if (checkIsNative()) return undefined;
    document.addEventListener('mouseleave', handleTouchEnd);
    document.addEventListener('mouseup', handleTouchEnd);
    return () => {
      document.removeEventListener('mouseleave', handleTouchEnd);
      document.removeEventListener('mouseup', handleTouchEnd);
    };
  }, [handleTouchEnd]);

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchEnd,
    onMouseDown: handleTouchStart,
    onMouseMove: handleTouchMove,
  };
};

export default useLongPress;
